import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import {
  jobClientSelector,
  jobClientsSelector,
} from '@model/Job/Job.selectors';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { forwardRef, useImperativeHandle, useState } from 'react';
import type {
  TAlertDetail,
  TShareLinkModalProps,
} from '@components/ShareLinkModal/ShareLinkModal.types';
import { selectorRootActions } from '@model/index';
import share from '@app/assets/images/share.jpg';
import akiShare from '@app/assets/images/aaron-king/modal-share.jpg';
import { ACTION_VARIANTS, Button, ICON_NAMES } from '@reverse-hr/design-system';
import { useTranslation } from 'react-i18next';
import { ShareLinkModalForm } from '@components/ShareLinkModal/_partials/ShareLinkModalForm';
import { ShareLinkModalTable } from '@components/ShareLinkModal/_partials/ShareLinkModalTable';
import { ShareLinkModalAlert } from '@components/ShareLinkModal/_partials/ShareLinkModalAlert';

export const ShareLinkModal = forwardRef((props: TShareLinkModalProps, ref) => {
  const { className } = props;
  const [alertDetail, setAlertDetail] = useState<TAlertDetail>({
    visible: false,
    type: 'default',
    icon: 'icn-info-circle-outline',
    children: '',
  });

  const { t } = useTranslation('', {
    keyPrefix: 'modal.share',
  });

  const jobClients = useStoreState(jobClientsSelector);
  const jobMainClient = useStoreState(jobClientSelector);
  const { setModalOpened, setModalId } = useStoreActions(selectorRootActions);

  const { entityId, jobId } = useParams<{ jobId: string; entityId: string }>();

  const hasClients = jobClients?.length > 0;
  const shareLinkJobId = entityId || jobId;

  const wrapperHeightClassName = classNames('c-share-link-modal', className);

  const handleCloseModal = (): void => {
    setModalOpened(false);
    setModalId(null);
  };

  useImperativeHandle(ref, () => ({
    cleanModal() {
      handleCloseModal();
    },
  }));

  return (
    <div className={wrapperHeightClassName}>
      <div className="c-share-link-modal__image">
        <img
          alt={t('imageAlt')}
          src={__ENVIRONMENT_CONFIG__.IS_THEME_AKI ? akiShare : share}
        />
      </div>
      <div className="c-share-link-modal__content">
        <div className="c-share-link-modal__close">
          <Button
            leftIcon={ICON_NAMES.CLOSE_BIG}
            variant={ACTION_VARIANTS.OUTLINED}
            onClick={handleCloseModal}
          />
        </div>
        <div className="rev-o-container rev-o-container--full">
          <div className="rev-o-grid">
            <div className="rev-o-col-12 rev-u-px-16">
              <p className="c-share-link-modal__title">{t('title')}</p>
            </div>
          </div>
        </div>

        <ShareLinkModalForm setAlertDetail={setAlertDetail} />

        <ShareLinkModalAlert
          alertDetail={alertDetail}
          setAlertDetail={setAlertDetail}
        />

        {jobMainClient && hasClients && shareLinkJobId && (
          <div className="rev-o-container rev-o-container--full">
            <div className="rev-o-grid">
              <div className="rev-u-px-16 rev-o-col-12 rev-o-col--medium-12">
                <>
                  <p className="c-share-link-modal__table-title">
                    {t('shared_with')}
                  </p>

                  <ShareLinkModalTable
                    items={jobClients}
                    mainRecipient={jobMainClient}
                    jobId={shareLinkJobId}
                    setAlertDetail={setAlertDetail}
                  />
                </>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
